import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/react-hooks';
import { Formik, Form } from 'formik';
import Lottie from 'lottie-react';

import { REQUEST_MAGIC_LINK } from 'apollo/mutations';
import { useTabSettings } from 'utils/hooks/useTabSettings';
import { useValidateFields } from 'components/evieOnboarding/utils/useValidateFields';
import { useToggle } from 'utils/hooks/useToggle';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { handleTrackEvent } from 'utils/analytics';
import showNotification from 'utils/showNotification';
import { ROUTES } from 'utils/constants';
import { KEYS } from 'components/evieOnboarding/utils/constants';
import { maskEmail } from 'utils/string';
import signupLogo from 'assets/images/signup-logo.webp';
import heartLoader from 'assets/heart-loader.json';

import Layout from 'components/evieOnboarding/Layout';
import TextInput from 'components/Surveys/FormControlSurveys/FormComponents/TextInput';
import SolidButton from 'components/SolidButton';

import './evie-login.scss';

// Passwordless login for individual patients (EVI-89). The backend response
// is identical for every email on purpose (enumeration safety), so the
// check-your-email state shows unconditionally after a request.
const EvieLogin = () => {
  useTabSettings('Evie', '/evie-favicon.ico');

  const { t } = useTranslation();
  const { push } = useHistory();

  const [userToken] = useLocalStorage(KEYS.USER_TOKEN);
  const { setEmail } = useValidateFields();
  const [requestMagicLink] = useMutation(REQUEST_MAGIC_LINK);
  const [spinner, setSpinner] = useToggle();
  const [sentTo, setSentTo] = useState(null);

  useEffect(() => {
    if (userToken) {
      push(ROUTES.EVIE_ASSISTANT);
    }
  }, [userToken]);

  const sendLink = async (email) => {
    setSpinner();
    try {
      await requestMagicLink({ variables: { email } });
      handleTrackEvent('magic_link_requested');
      setSentTo(email);
    } catch {
      showNotification({ messages: [t('evieApp.magicLink.requestFailed')] });
    } finally {
      setSpinner();
    }
  };

  const renderEmailForm = () => (
    <>
      <h2 className="evie-login__container_title">{t('evieApp.magicLink.title')}</h2>
      <p className="evie-login__container_description">{t('evieApp.magicLink.description')}</p>
      <Formik initialValues={{ email: '' }} validationSchema={setEmail} onSubmit={({ email }) => sendLink(email)}>
        {(formData) => (
          <Form style={{ width: '100%' }}>
            <div className="form-input evie-login__loginform imput-password">
              <TextInput
                validate
                placeholder={t('evieOnboarding.signup.emailPlaceholder')}
                formikProps={formData}
                name="email"
              />
            </div>
            {/* A second tap issues a second link, and the newer one
                invalidates the first — so the email she opens is the dead one. */}
            <SolidButton disabled={spinner} type="submit" className="evie-survey">
              {t('evieApp.magicLink.cta')}
            </SolidButton>
          </Form>
        )}
      </Formik>
      <p className="evie-login__trust">{t('evieApp.magicLink.trust')}</p>
      <p className="evie-login__signup">
        {t('evieApp.magicLink.signupPrompt')}{' '}
        <button type="button" className="evie-login__textlink" onClick={() => push(ROUTES.EVIE_ONBOARDING)}>
          {t('evieApp.magicLink.signupCta')}
        </button>
      </p>
    </>
  );

  const renderCheckEmail = () => (
    <>
      <p className="evie-login__envelope" role="img" aria-label="email sent">
        ✉️
      </p>
      <h2 className="evie-login__container_title">{t('evieApp.magicLink.sentTitle')}</h2>
      <p className="evie-login__masked">{maskEmail(sentTo)}</p>
      <p className="evie-login__container_description">{t('evieApp.magicLink.sentDescription')}</p>
      <p className="evie-login__spam">{t('evieApp.magicLink.sentSpam')}</p>
      <button type="button" className="evie-login__textlink" onClick={() => setSentTo(null)}>
        {t('evieApp.magicLink.changeEmail')}
      </button>
    </>
  );

  return (
    <>
      <Layout>
        <main className="evie-login">
          <section className="evie-login__container">
            <img className="evie-login__container_logo" src={signupLogo} alt="Evie logo" />
            {sentTo ? renderCheckEmail() : renderEmailForm()}
          </section>
        </main>
      </Layout>
      {spinner && (
        <div className="onboarding-layout__lottie">
          <Lottie animationData={heartLoader} loop style={{ width: 140, height: 110 }} />
        </div>
      )}
    </>
  );
};

export default EvieLogin;
